import {
  ApiKeySchema,
  OrganizationSchema,
  PaymentPlanCardTiers,
  PaymentPlanTier,
  paymentPlanCardTiers,
} from '@/types/schema';

import { STARTUP_APPROVAL_VALIDITY_DAYS } from './constants';

export function getDisplayDateFromEndOfBillingPeriod(
  endOfBillingPeriod: string
) {
  const lastDayOfBillingPeriod = new Date(endOfBillingPeriod);
  lastDayOfBillingPeriod.setDate(lastDayOfBillingPeriod.getDate() - 1); // Subtract 1 day for clarity, because it ends at midnight UTC of the next day
  return lastDayOfBillingPeriod.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function hasValidStartupApproval(
  organization: OrganizationSchema | undefined
): boolean {
  const approvalDate = organization?.config?.startup_plan_approval_date;
  const date180DaysAgo = new Date();

  date180DaysAgo.setDate(
    date180DaysAgo.getDate() - STARTUP_APPROVAL_VALIDITY_DAYS
  );

  const isApprovedForStartup =
    !!approvalDate && new Date(approvalDate) > date180DaysAgo;

  return isApprovedForStartup;
}

export function hasValidApprovals(
  organization: OrganizationSchema | undefined
): {
  startup: boolean;
  partner: boolean;
  premier: boolean;
} {
  return {
    startup: hasValidStartupApproval(organization),
    premier: !!organization?.config.premier_plan_approval_date,
    partner: !!organization?.config.partner_plan_approval_date,
  };
}

export const formatter = new Intl.NumberFormat('en-US', {});
export const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

type GetOptionalTierParams = {
  currentTier: PaymentPlanTier | undefined;
  upcomingTier: PaymentPlanTier | undefined;
  approvals: {
    startup: boolean;
    partner: boolean;
    premier: boolean;
  };
};

export const getOptionalTier = ({
  currentTier,
  upcomingTier,
  approvals,
}: GetOptionalTierParams): PaymentPlanCardTiers | null => {
  const tierOrder = Object.keys(paymentPlanCardTiers);
  const upcomingTierRank = tierOrder.indexOf(upcomingTier || '');
  const currentTierRank = tierOrder.indexOf(currentTier || '');
  // implied approval order is startup -> partner -> premier
  const highestApprovedTier = Object.keys(approvals)
    // don't ever show partner card even if there is approval
    .filter((key) => approvals[key] && key !== 'partner')
    .slice(-1)[0];
  const highestApprovedTierRank = tierOrder.indexOf(highestApprovedTier);

  const highestRank = Math.max(upcomingTierRank, highestApprovedTierRank);
  if (highestRank === -1) {
    return currentTierRank >= tierOrder.indexOf('applyStartup')
      ? null
      : 'applyStartup';
  }
  if (currentTierRank >= highestRank) {
    return null;
  }

  return tierOrder[highestRank] as PaymentPlanCardTiers;
};

export const validateBackfillDate = (value: string | undefined) => {
  if (!value) return true;
  const selectedDate = new Date(value);
  const now = new Date();
  const year2020 = new Date('2020-01-01');
  if (isNaN(selectedDate.getTime())) {
    return 'Invalid date';
  }
  if (selectedDate >= now) {
    return 'Date must be in the past';
  }
  if (selectedDate < year2020) {
    return 'Date must be after Jan 1, 2020';
  }
  return true;
};
export function compareKeys(a: ApiKeySchema, b: ApiKeySchema) {
  // If the key has a last used date, sort by that
  if (a.last_used_at && b.last_used_at) {
    const aDate = new Date(
      a.last_used_at.includes('Z') ? a.last_used_at : a.last_used_at + 'Z'
    ).valueOf();
    const bDate = new Date(
      b.last_used_at.includes('Z') ? b.last_used_at : b.last_used_at + 'Z'
    ).valueOf();
    return bDate - aDate;
  }
  if (!a.last_used_at && b.last_used_at) return 1;
  if (a.last_used_at && !b.last_used_at) return -1;
  // If the key has no last used date, sort by created
  if (!a.created_at || !b.created_at) return 0;
  const aDate = new Date(
    a.created_at.includes('Z') ? a.created_at : a.created_at + 'Z'
  ).valueOf();
  const bDate = new Date(
    b.created_at.includes('Z') ? b.created_at : b.created_at + 'Z'
  ).valueOf();
  return bDate - aDate;
}

export const isLowerTier = (
  tier1: PaymentPlanCardTiers | PaymentPlanTier | undefined,
  tier2: PaymentPlanCardTiers | PaymentPlanTier | undefined
) => {
  if (!tier1) return true;
  if (!tier2) return false;
  const tierOrder = Object.keys(paymentPlanCardTiers);
  const tier1Rank = tierOrder.indexOf(tier1);
  const tier2Rank = tierOrder.indexOf(tier2);
  return tier1Rank < tier2Rank;
};
